import type { KeyValueStorage } from '../auth/session.js'

/**
 * Выбор темы. `system` — не третья тема, а отказ выбирать: портал берёт
 * тему из настроек операционной системы и меняется вместе с ней.
 */
export type ThemeChoice = 'system' | 'light' | 'dark'

export interface ThemeStore {
  /** Что выбрано сейчас. `system`, пока человек не выбирал сам. */
  choice(): ThemeChoice
  /** Запоминает выбор и применяет его к странице немедленно. */
  set(choice: ThemeChoice): void
}

/**
 * Корень документа глазами хранилища темы. Интерфейс вместо `HTMLElement`
 * не ради подмены в тестах (там настоящий `document.documentElement`), а
 * ради honest-границы: теме от корня нужны ровно два действия, и знать про
 * остальной DOM ей незачем.
 */
export interface ThemeRoot {
  setAttribute(name: string, value: string): void
  removeAttribute(name: string): void
}

const STORAGE_KEY = 'rk-admin.theme'
const ATTRIBUTE = 'data-theme'

const CHOICES: ReadonlySet<string> = new Set<ThemeChoice>(['system', 'light', 'dark'])

function isChoice(value: string | undefined | null): value is ThemeChoice {
  return value !== undefined && value !== null && CHOICES.has(value)
}

/**
 * Хранилище выбора темы.
 *
 * Явный выбор живёт атрибутом `data-theme` на корне документа, а системная
 * тема — отсутствием этого атрибута: таблица стилей написана так, что
 * тёмные значения токенов действуют и по `[data-theme="dark"]`, и по
 * `@media (prefers-color-scheme: dark)`, если только не выбрана светлая
 * явно. Поэтому «системная» тема — это буквально снятый атрибут, а не
 * третье значение в CSS: браузер сам следит за настройкой системы и
 * перекрашивает страницу без единой строки на JS.
 *
 * Выбор переживает перезагрузку (localStorage): тема — свойство рабочего
 * места, а не вкладки, и заново выбирать её при каждом открытии портала
 * человек не должен.
 */
export function createThemeStore(storage: KeyValueStorage, root: ThemeRoot): ThemeStore {
  const stored = storage.get(STORAGE_KEY)
  // Несовместимое значение (чужая версия портала, правка руками) — то же
  // самое, что отсутствие выбора: сломанная строка в хранилище не повод
  // отказывать человеку в оформлении.
  let current: ThemeChoice = isChoice(stored) ? stored : 'system'

  function apply(choice: ThemeChoice): void {
    if (choice === 'system') root.removeAttribute(ATTRIBUTE)
    else root.setAttribute(ATTRIBUTE, choice)
  }

  apply(current)

  return {
    choice: () => current,
    set(choice: ThemeChoice) {
      current = choice
      apply(choice)
      // Системная тема стирает ключ, а не пишет 'system': запись «выбрано
      // системное» и отсутствие записи означают одно и то же, а два
      // способа сказать одно и то же со временем разъезжаются.
      if (choice === 'system') storage.delete(STORAGE_KEY)
      else storage.set(STORAGE_KEY, choice)
    },
  }
}
